import {
  LoadIcon,
  save,
  exportToFileIcon,
  usersIcon,
  eyeIcon,
  PlusIcon,
  DuplicateIcon,
} from "@excalidraw/excalidraw/components/icons";
import { MainMenu } from "@excalidraw/excalidraw/index";
import React from "react";

import { isDevEnv } from "@excalidraw/common";

import type { Theme } from "@excalidraw/element/types";

import { LanguageList } from "../app-language/LanguageList";
import { useAtomValue, useSetAtom } from "../app-jotai";
import { AUTH_ENABLED } from "../auth/authConfig";
import {
  saveCurrentCloudDoc,
  setAutosaveEnabled,
  newDrawing,
  duplicateDrawing,
  currentCloudDocAtom,
} from "../cloud/cloudActions";
import {
  openChooserDialogAtom,
  versionHistoryDialogAtom,
} from "../cloud/dialogState";

import { saveDebugState } from "./DebugCanvas";

// Primary cloud action shown at the top of the menu (below New/Open/Save to).
// "Duplicate" keeps the current canvas but detaches it from its cloud document
// so it can be saved as a brand-new drawing (to disk or cloud).
const CloudPrimaryItems: React.FC = () => {
  const current = useAtomValue(currentCloudDocAtom);

  // Only meaningful when the open drawing is currently a cloud document.
  if (current == null) {
    return null;
  }

  return (
    <MainMenu.Item icon={DuplicateIcon} onSelect={() => duplicateDrawing()}>
      Duplicate
    </MainMenu.Item>
  );
};

// Secondary cloud actions (only meaningful for an open cloud document).
const CloudSecondaryItems: React.FC = () => {
  const current = useAtomValue(currentCloudDocAtom);
  const openVersionDialog = useSetAtom(versionHistoryDialogAtom);

  if (current == null) {
    return null;
  }

  return (
    <>
      <MainMenu.Separator />
      <MainMenu.Item
        icon={save}
        onSelect={() => {
          const description = window.prompt(
            "Describe this version (optional)",
            "",
          );
          if (description !== null) {
            void saveCurrentCloudDoc("tagged", description || null);
          }
        }}
      >
        Save version…
      </MainMenu.Item>
      <MainMenu.Item
        icon={current.autosave ? eyeIcon : exportToFileIcon}
        onSelect={() => void setAutosaveEnabled(!current.autosave)}
      >
        {current.autosave ? "Autosave: On" : "Autosave: Off"}
      </MainMenu.Item>
      <MainMenu.Item
        icon={usersIcon}
        onSelect={() =>
          openVersionDialog({
            isOpen: true,
            docId: current.docId,
            name: current.name,
          })
        }
      >
        Version history
      </MainMenu.Item>
    </>
  );
};

export const AppMainMenu: React.FC<{
  onCollabDialogOpen: () => any;
  isCollaborating: boolean;
  isCollabEnabled: boolean;
  theme: Theme | "system";
  refresh: () => void;
}> = React.memo((props) => {
  const openChooser = useSetAtom(openChooserDialogAtom);
  return (
    <MainMenu>
      {AUTH_ENABLED && (
        <MainMenu.Item
          icon={PlusIcon}
          onSelect={() => {
            if (
              window.confirm(
                "Start a new drawing? This clears the canvas. Save your current work first if you need it.",
              )
            ) {
              newDrawing();
            }
          }}
        >
          New
        </MainMenu.Item>
      )}
      {AUTH_ENABLED ? (
        <MainMenu.Item icon={LoadIcon} onSelect={() => openChooser(true)}>
          Open
        </MainMenu.Item>
      ) : (
        <MainMenu.DefaultItems.LoadScene />
      )}
      <MainMenu.DefaultItems.SaveToActiveFile />
      {AUTH_ENABLED && <CloudPrimaryItems />}
      <MainMenu.DefaultItems.Export />
      <MainMenu.DefaultItems.SaveAsImage />
      <MainMenu.DefaultItems.CommandPalette className="highlighted" />
      <MainMenu.DefaultItems.SearchMenu />
      <MainMenu.DefaultItems.Help />
      <MainMenu.DefaultItems.ClearCanvas />
      {AUTH_ENABLED && <CloudSecondaryItems />}
      {isDevEnv() && (
        <MainMenu.Item
          icon={eyeIcon}
          onSelect={() => {
            if (window.visualDebug) {
              delete window.visualDebug;
              saveDebugState({ enabled: false });
            } else {
              window.visualDebug = { data: [] };
              saveDebugState({ enabled: true });
            }
            props?.refresh();
          }}
        >
          Visual Debug
        </MainMenu.Item>
      )}
      <MainMenu.Separator />
      <MainMenu.DefaultItems.Preferences />
      <MainMenu.DefaultItems.ToggleTheme allowSystemTheme theme={props.theme} />
      <MainMenu.ItemCustom>
        <LanguageList style={{ width: "100%" }} />
      </MainMenu.ItemCustom>
      <MainMenu.DefaultItems.ChangeCanvasBackground />
    </MainMenu>
  );
});
